<script setup lang="ts">
import { storeToRefs } from 'pinia'

import InfluencerProfileBanner from '~/components/influencer/profile/Banner.vue'
import InfluencerProfileHeaderNavigation from '~/components/influencer/profile/header/Navigation.vue'
import InfluencerTextBadge from '~/components/influencer/profile/header/TextBadge.vue'
import VBookmark from '~/components/ui/VBookmark.vue'

import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'
import { useNavigateToPreviousPage } from '~/composables/useNavigateToPreviousPage'

import { useDraftStore } from '~/stores/draft'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useTagStore } from '~/stores/tag'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import { Breakpoints } from '~/enums/breakpoints'

import type Tag from '~/entities/tag'
import type { Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer
  userIsOwner: boolean
  hasInteraction: boolean
  showAbout: boolean
  showInteraction: boolean
}

type Emits = {
  toggle: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const route = useRoute()
const { t } = useI18n()
const { navigateToPreviousPage } = useNavigateToPreviousPage()
const { curatorIsContest } = useCuratorContestsInfos(props.influencer)

const { id: DRAFT_ID } = storeToRefs(useDraftStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { GET_TAG_FROM_ID, GET_TAGS_FROM_IDS } = useTagStore()

const displayBucketList = ref(false)
const banner = ref<InstanceType<typeof InfluencerProfileBanner> | null>(null)

const { picture } = useGetUserProfilePicture()

const isContest = computed(() => curatorIsContest(props.influencer))

const kinds = computed(() => {
  if (isContest.value) return t(`tags.influencer_kind.springboard`)

  return GET_TAGS_FROM_IDS(
    props.influencer.tags?.identity.influencer_kind ?? [],
  )
    .map((kindTag: Tag) => {
      return t(`tags.influencer_kind.${kindTag.name}`)
    })
    .join(', ')
})

const country = computed(() => {
  if (!props.influencer.tags?.identity.country) return undefined

  const tag = GET_TAG_FROM_ID(props.influencer.tags?.identity.country[0] ?? 0)
  if (!tag) return undefined

  return t(`tags.country.${tag.name}`)
})

function editBanner() {
  banner.value?.addPicture()
}

function toggle() {
  if (props.hasInteraction) emit('toggle')
}

const { handleChevronClick } = useCuratorProfilePreviousButton()

// inline composable for the profile picture logic
function useGetUserProfilePicture() {
  const { timestamp } = useGetServerSyncedTimestamp('header-profile-image')
  const { getInfProfilePictureForSlug } = useGetProfilePicture()
  const { has_profile_picture: HAS_PROFILE_PICTURE } = storeToRefs(
    useUserInfluencerStore(),
  )

  const picture = computed(() => {
    return getInfProfilePictureForSlug(
      props.influencer.slug,
      { target: 'profile_picture', size: '400_400' },
      timestamp.value,
    )
  })

  watch(HAS_PROFILE_PICTURE, (newValue) => {
    if (!newValue) return
    timestamp.value++
  })

  return {
    picture,
  }
}
</script>

<template>
  <article class="profileHeaderMainWrapper" :class="{ isContest }">
    <div class="topContent">
      <InfluencerProfileBanner
        ref="banner"
        class="banner"
        :influencer="influencer"
        :can-edit="userIsOwner"
      />
      <div v-if="SCREEN_WIDTH < Breakpoints.lg" class="contentWrap">
        <button
          v-if="userIsOwner"
          type="button"
          class="editBannerText"
          @click="editBanner"
        >
          <i class="fas fa-edit" />
          <span class="editBannerSpan">{{
            $t('influencer.profile.slug.profile.nav06')
          }}</span>
        </button>
        <div v-if="!userIsOwner" class="iconWrap">
          <button
            type="button"
            :aria-label="$t('common.goBack').toString()"
            @click="handleChevronClick"
          >
            <i class="fas fa-chevron-left icon" aria-hidden />
          </button>
          <VBookmark
            v-if="SCREEN_WIDTH < Breakpoints.md"
            :influencer="influencer"
            :hide-tooltip-arrow="true"
          />
        </div>
        <div class="profilePic">
          <img :src="picture" alt="" />
        </div>
        <div class="influencerNameWrapper">
          <h1 class="influencerName ellipsis">{{ influencer.entity }}</h1>
          <InfluencerTextBadge :influencer="influencer" />
        </div>
        <div class="influencerInfo">
          <span class="influencerType">{{ kinds }}</span>
          <span v-if="SCREEN_WIDTH >= Breakpoints.md && country" class="period"
            >·</span
          >
          <span class="influencerLocation">{{ country }}</span>
        </div>
      </div>
      <div
        v-if="SCREEN_WIDTH >= Breakpoints.lg"
        class="contentWrap mainParentContainer"
      >
        <div class="picHolder">
          <div class="profilePic">
            <img :src="picture" alt="" />
          </div>
        </div>
        <div class="textWrap ellipsis">
          <div class="influencerNameWrapper">
            <h1 class="influencerName ellipsis">{{ influencer.entity }}</h1>
            <InfluencerTextBadge :influencer="influencer" />
          </div>
          <div class="influencerInfo">
            <span class="influencerType ellipsis">{{ kinds }}</span>
            <span v-if="country" class="period">·</span>
            <span class="influencerLocation">{{ country }}</span>
          </div>
        </div>
        <button
          v-if="userIsOwner"
          type="button"
          class="editBannerText"
          @click="editBanner"
        >
          <i class="fas fa-edit" />
          <span class="editBannerSpan">{{
            $t('influencer.profile.slug.profile.nav06')
          }}</span>
        </button>
      </div>
    </div>
    <InfluencerProfileHeaderNavigation
      v-model="displayBucketList"
      :influencer="influencer"
      :user-is-owner="userIsOwner"
      :show-about="showAbout"
      :has-interaction="hasInteraction"
      :show-interaction="showInteraction"
      @toggle="toggle"
    />
  </article>
</template>

<style scoped>
.profileHeaderMainWrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.topContent {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.banner {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: var(--color-main);
}

.contentWrap {
  padding: 24px;
  position: relative;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.iconWrap {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.icon {
  font-size: var(--font-size-title-micro);
  color: var(--color-white);
  cursor: pointer;
}

.profilePic {
  height: 160px;
  width: 160px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 24px;
}

.isContest .profilePic {
  border-radius: 2px;
  box-shadow: var(--shadow-picture);
}

.profilePic > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.influencerNameWrapper {
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  position: relative;
}

.influencerName {
  font-size: var(--font-size-title);
  font-weight: 600;
  color: var(--color-white);
  text-align: center;
  width: auto;
  max-width: calc(100% - 100px);
}

.influencerInfo {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.influencerType {
  text-transform: capitalize;
  font-size: var(--font-size-big);
  font-weight: 600;
  color: var(--color-white);
  margin-bottom: 4px;
  width: auto;
}

.influencerLocation {
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--color-white);
}

.navBar {
  display: flex;
  align-items: center;
  background: var(--color-white);
  box-shadow: var(--shadow-regular-dark);
}

.navContent {
  display: flex;
  align-items: center;
}

.selectorWrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.selectorWrapper.padded {
  padding: 16px 0px;
  justify-content: center;
}

.selectors {
  display: flex;
  width: 100%;
}

.selectorElem {
  padding: 24px;
  width: 50%;
  text-align: center;
  cursor: pointer;
}

.selectorElem.noInteraction {
  cursor: default;
}

.selectorText {
  color: var(--color-black);
  font-size: var(--font-size-big);
}

.selectorSpan {
  font-size: var(--font-size-big);
}

.selectorText.noInteraction {
  color: var(--color-darker-grey);
}

.selected {
  color: var(--color-main);
  font-weight: 600;
}

.redBar {
  position: relative;
  width: calc(100% + 48px);
  top: 24px;
  right: 24px;
  height: 2px;
  background-color: var(--color-main);
}

.mainBtnText {
  font-size: var(--font-size-small);
  font-weight: 600;
}

.editBannerText {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.fa-edit {
  font-size: var(--font-size-small);
  color: var(--color-white);
  opacity: 0.75;
  margin-right: 8px;
}

.editBannerSpan {
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--color-white);
  opacity: 0.75;
}

.influencerTextBadge {
  margin-left: 16px;
  flex-shrink: 0;
}

.picHolder {
  flex-shrink: 0;
}

@media screen and (min-width: 375px) {
  .mainBtnText {
    font-size: var(--font-size-big);
    font-weight: 600;
  }
}

@media screen and (min-width: 480px) {
  .period {
    font-size: var(--font-size-big);
    font-weight: 600;
    color: var(--color-white);
    margin-left: 4px;
    margin-right: 4px;
  }
}

@media screen and (min-width: 768px) {
  .influencerInfo {
    flex-direction: row;
  }

  .influencerType {
    margin-bottom: unset;
  }

  .navBar {
    min-height: 83px;
  }

  .fa-bookmark {
    color: var(--color-main);
    margin-left: auto;
  }

  .selectorElem {
    padding: 32px 24px;
    width: auto;
    text-align: center;
    cursor: pointer;
  }

  .redBar {
    top: 32px;
    right: 24px;
    width: calc(100% + 48px);
  }

  .fas.fa-bookmark,
  .far.fa-bookmark {
    padding: 16px;
  }
}

@media screen and (min-width: 1024px) {
  .topContent {
    min-height: 200px;
    justify-content: unset;
    align-items: flex-end;
  }

  .contentWrap {
    padding: 0px 0px 24px 0px;
    flex-direction: row;
    justify-content: unset;
    align-items: flex-end;
  }

  .picHolder {
    position: relative;
    width: 160px;
    height: 80px;
    margin-right: 24px;
  }

  .profilePic {
    position: absolute;
    top: 0;
    left: 0;
    box-shadow: var(--shadow-regular);
    margin-bottom: unset;
  }

  .textWrap {
    display: flex;
    flex-direction: column;
  }

  .fa-chevron-left {
    position: absolute;
    color: var(--color-darker-grey);
    font-size: var(--font-size-huge);
    margin-left: -32px;
  }

  .selectorWrapper {
    margin-left: calc(160px + 24px);
  }

  .selectorWrapper.padded {
    justify-content: flex-end;
  }

  .editBannerText {
    display: flex;
    margin-left: 16px;
  }

  .influencerType {
    text-align: left;
  }

  .influencerNameWrapper {
    width: unset;
    justify-content: flex-start;
  }
}
</style>
